import { Capacitor } from '@capacitor/core';
import { SocialLogin, type AppleProviderResponse } from '@capgo/capacitor-social-login';
import { AppsFlyer } from 'appsflyer-capacitor-plugin';
import { logEvent, logMetaPixelGoogleWebLoginOutcome } from './analytics';

import { getRealtimeVoiceServerUrl } from './realtimeVoice/config';

import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  browserLocalPersistence,
  setPersistence,
  getAdditionalUserInfo,
  type UserCredential,
} from 'firebase/auth';

function getFirebaseApp() {
  if (getApps().length > 0) return getApps()[0];

  // authDomain 优先级：
  // 1. 显式配置的 VITE_FIREBASE_AUTH_DOMAIN
  // 2. 当前页面域名（Vercel 已经有 /__/auth/* 反代，避免 *.firebaseapp.com 触发 iOS Universal Links 中间页）
  // 3. 兜底：firebaseapp.com 默认域（会导致移动端出现 "Continue in App" 中间页）
  const explicitDomain = (import.meta as any).env?.VITE_FIREBASE_AUTH_DOMAIN as string | undefined;
  const currentHostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
  const authDomain =
    explicitDomain?.trim() ||
    (currentHostname && currentHostname !== 'localhost' ? currentHostname : undefined) ||
    `${(import.meta as any).env?.VITE_FIREBASE_PROJECT_ID}.firebaseapp.com`;

  return initializeApp({
    apiKey: (import.meta as any).env?.VITE_FIREBASE_API_KEY,
    projectId: (import.meta as any).env?.VITE_FIREBASE_PROJECT_ID,
    appId: (import.meta as any).env?.VITE_FIREBASE_APP_ID,
    authDomain,
  });
}

// iOS Safari 会拦截 popup，移动端必须用 redirect；桌面端用 popup（Chrome 对 redirect 的跨域存储越来越严格，容易静默失败）
function isMobileBrowser(): boolean {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/** 是否首次在 Firebase 创建该账号（官方 API，见 getAdditionalUserInfo） */
function isFirebaseGoogleNewUser(credential: UserCredential): boolean {
  const info = getAdditionalUserInfo(credential)
  return info?.isNewUser === true;
}

export async function loginWithGoogleWeb(): Promise<AuthUser> {
  const app = getFirebaseApp();
  const auth = getAuth(app);
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');

  await setPersistence(auth, browserLocalPersistence);

  if (isMobileBrowser()) {
    // 强制显示 Google 账号选择框，防止 logout 后自动用上次账号登录
    provider.setCustomParameters({ prompt: 'select_account' });
    // 移动端：redirect 流程（配合 vercel.json 反代 /__/auth/*，解决 iOS Safari ITP 问题）
    await signInWithRedirect(auth, provider);
    return new Promise(() => {}); // 页面会跳转，不会执行到这里
  }

  // 桌面端：popup 流程（更稳定，不受 Chrome 跨域存储策略影响）
  const result = await signInWithPopup(auth, provider);
  const firebaseUser = result.user;
  logMetaPixelGoogleWebLoginOutcome(isFirebaseGoogleNewUser(result), firebaseUser.uid, firebaseUser.email);
  return {
    id: firebaseUser.uid,
    email: firebaseUser.email ?? undefined,
    full_name: firebaseUser.displayName ?? undefined,
    profile_photo_url: firebaseUser.photoURL ?? undefined,
  };
}

export async function handleGoogleRedirectResult(): Promise<{ user: AuthUser; token: JWTTokenResponse } | null> {
  const app = getFirebaseApp();
  const auth = getAuth(app);
  const result = await getRedirectResult(auth);
  if (!result) return null;
  const firebaseUser = result.user;
  logMetaPixelGoogleWebLoginOutcome(isFirebaseGoogleNewUser(result), firebaseUser.uid, firebaseUser.email);
  const authUser: AuthUser = {
    id: firebaseUser.uid,
    email: firebaseUser.email ?? undefined,
    full_name: firebaseUser.displayName ?? undefined,
    profile_photo_url: firebaseUser.photoURL ?? undefined,
  };
  saveAuthUser(authUser);
  const token = await getJWTTokenFromBackend(authUser, 'google');
  if (token.is_whitelisted !== undefined) {
    authUser.is_whitelisted = token.is_whitelisted;
    saveAuthUser(authUser);
  }
  saveJWTToken(token.jwt_token);
  return { user: authUser, token };
}

export type AuthUser = {
  id: string;
  email?: string;
  username?: string;
  full_name?: string;
  nickname?: string;
  profile_photo_url?: string;
  is_whitelisted?: boolean;
  is_admin?: boolean;
  has_completed_tutorial?: boolean;
};

export type JWTTokenResponse = {
  jwt_token: string;
  token_type: string;
  expires_in: number;
  user_id: string;
  is_whitelisted: boolean;
};

/** Google / Apple OAuth 在网页端使用的回调地址，需与控制台配置一致；默认固定为登录页 /splash */
function getWebOAuthRedirectUri(envKey: 'VITE_GOOGLE_WEB_REDIRECT_URI' | 'VITE_APPLE_WEB_REDIRECT_URI'): string {
  const custom = (import.meta as any).env?.[envKey] as string | undefined;
  if (custom?.trim()) return custom.trim();
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/splash`;
}

function decodeJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Failed to decode JWT:', e);
    return null;
  }
}

export async function loginWithGoogle(): Promise<AuthUser> {
  const platform = Capacitor.getPlatform();

  // Web 平台使用 Firebase Auth（支持手机端 redirect 登录）
  if (platform === 'web') {
    return loginWithGoogleWeb();
  }

  const IOS_CLIENT_ID = (import.meta as any).env?.VITE_GOOGLE_IOS_CLIENT_ID as string | undefined;
  const WEB_CLIENT_ID = (import.meta as any).env?.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined;

  if (platform === 'ios' && !IOS_CLIENT_ID) {
    throw new Error('Missing VITE_GOOGLE_IOS_CLIENT_ID');
  }
  if (platform === 'android' && !WEB_CLIENT_ID) {
    throw new Error('Missing VITE_GOOGLE_WEB_CLIENT_ID');
  }

  // 初始化 Google Provider（iOS 需要 iOSClientId；Android/Web 需要 webClientId）
  await SocialLogin.initialize({
    google: {
      ...(IOS_CLIENT_ID ? { iOSClientId: IOS_CLIENT_ID } : {}),
      ...(WEB_CLIENT_ID ? { webClientId: WEB_CLIENT_ID } : {}),
      ...(platform === 'web' ? { redirectUrl: getWebOAuthRedirectUri('VITE_GOOGLE_WEB_REDIRECT_URI') } : {}),
      // mode: 'online' // 如需 offline 授权，需同时提供 iOSServerClientId = webClientId
    }
  }).catch((e: any) => { throw e; });

  const googleLogin = await SocialLogin.login({
    provider: 'google',
    options: {
      scopes: ['email', 'profile']
    }
  }).catch((e: any) => { throw e; });

  console.log('Google login raw result:', JSON.stringify(googleLogin));

  // The structure seems to be { provider: 'google', result: { responseType: 'online', profile: { ... }, idToken: '...' } }
  // or sometimes flat depending on the platform/version. We handle both.
  const result: any = googleLogin;
  const innerResult = result.result || result;
  const profile = innerResult.profile || {};

  console.log('Google profile data:', JSON.stringify(profile));

  // 尝试从 idToken 中解析 sub (User ID)
  let decodedSub: string | undefined;
  const idToken = innerResult.idToken || innerResult.authentication?.idToken;

  if (idToken) {
    const decoded = decodeJwt(idToken);
    if (decoded?.sub) {
      decodedSub = decoded.sub;
      console.log('Decoded user ID from idToken:', decodedSub);
    }
  }

  // 优先使用 profile.id (iOS/Android)，其次 result.userId (Web)，然后是 decodedSub
  // 注意：iOS 上 profile.id 是 sub (OAuth ID)，result.userId 也是 sub
  // innerResult.accessToken.userId might also exist
  const userId = profile?.id || innerResult.userId || innerResult.accessToken?.userId || profile?.sub || decodedSub;

  if (!userId) {
    console.error('Google login failed: Missing user ID in response', { result, innerResult, profile });
    throw new Error('Google login failed: Could not retrieve user ID');
  }

  return {
    id: userId,
    email: profile?.email,
    full_name: profile?.name || profile?.displayName || profile?.givenName,
    profile_photo_url: profile?.photo || profile?.photoUrl || profile?.picture || profile?.imageUrl,
  };
}

function isAppleCancelError(err: any): boolean {
  // Capacitor plugin rejects with string code, not number — check both
  const code = err?.code ?? err?.error?.code ?? err?.details?.code;
  const domain = err?.domain ?? err?.error?.domain ?? err?.details?.domain;
  const message = (err?.message || '').toLowerCase();
  return (
    code === 1001 || code === '1001' || // ASAuthorizationError.canceled (number or string)
    message.includes('authorizationerror error 1001') ||
    message.includes('cancelled') ||
    message.includes('canceled') ||
    (typeof domain === 'string' && domain.toLowerCase().includes('authorization') && (code === 1001 || code === '1001'))
  );
}

function isAppleSystemError(err: any): boolean {
  // ASAuthorizationError codes other than cancel: 1000 (unknown), 1002 (invalidResponse),
  // 1003 (notHandled), 1004 (failed) — these are transient system errors, not user actions
  const code = err?.code ?? err?.error?.code ?? err?.details?.code;
  const domain = err?.domain ?? err?.error?.domain ?? err?.details?.domain;
  const numCode = typeof code === 'string' ? parseInt(code, 10) : code;
  return (
    typeof domain === 'string' &&
    domain.toLowerCase().includes('authenticationservices') &&
    typeof numCode === 'number' &&
    numCode !== 1001
  );
}

export async function loginWithApple(): Promise<AuthUser> {
  const platform = Capacitor.getPlatform();

  // 网页端：使用 Capgo SocialLogin 的 Apple Web（Sign in with Apple JS），需配置 Services ID 与重定向 URL
  if (platform === 'web') {
    const clientId = (import.meta as any).env?.VITE_APPLE_WEB_CLIENT_ID as string | undefined;
    if (!clientId) {
      throw new Error('Missing VITE_APPLE_WEB_CLIENT_ID');
    }
    const redirectUrl = getWebOAuthRedirectUri('VITE_APPLE_WEB_REDIRECT_URI');
    await SocialLogin.initialize({
      apple: { clientId, redirectUrl },
    });
    let appleRes: Awaited<ReturnType<typeof SocialLogin.login>>;
    try {
      appleRes = await SocialLogin.login({
        provider: 'apple',
        options: { scopes: ['email', 'name'] },
      });
    } catch (e: any) {
      const msg = String(e?.message || e || '').toLowerCase();
      if (msg.includes('cancel') || msg.includes('closed') || msg.includes('popup')) {
        const cancelErr = new Error('APPLE_SIGNIN_CANCELED');
        (cancelErr as any).code = 'APPLE_SIGNIN_CANCELED';
        (cancelErr as any).isCanceled = true;
        throw cancelErr;
      }
      throw e;
    }
    if (appleRes.provider !== 'apple') {
      throw new Error('Apple Sign-In failed: unexpected provider response');
    }
    const inner = appleRes.result as AppleProviderResponse;
    const profile = inner.profile;
    let userId = profile?.user;
    if (!userId && inner.idToken) {
      const decoded = decodeJwt(inner.idToken);
      userId = decoded?.sub;
    }
    if (!userId) {
      throw new Error('Apple Sign-In failed: No user identifier received from Apple');
    }
    const fullName = [profile?.givenName, profile?.familyName].filter(Boolean).join(' ').trim() || undefined;
    return {
      id: userId,
      email: profile?.email || undefined,
      full_name: fullName,
      profile_photo_url: undefined,
    };
  }

  const plugins = (window as any).Capacitor?.Plugins || (Capacitor as any)?.Plugins;
  const apple = plugins?.SignInWithApple;
  if (!apple?.authorize) {
    throw new Error('Capacitor Apple Sign-In plugin not available. Please install @capacitor-community/apple-sign-in and sync.');
  }
  let res: any;
  try {
    res = await apple.authorize();
  } catch (e: any) {
    // #region agent log H-B H-D: apple.authorize() error
    try{const _d={sessionId:'9ab244',loc:'auth.ts:loginWithApple:catch',msg:'apple.authorize threw',code:e?.code,codeType:typeof e?.code,domain:e?.domain,errMsg:e?.message,isCancel:isAppleCancelError(e),isSys:isAppleSystemError(e),ts:Date.now(),hyp:'H-B,H-D'};try{const _p=JSON.parse(localStorage.getItem('_dbg9ab244')||'[]');_p.push(_d);localStorage.setItem('_dbg9ab244',JSON.stringify(_p));}catch(_){}fetch('http://127.0.0.1:7791/ingest/26a0facd-5b8c-473d-9da8-40faa6cbd7e3',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'9ab244'},body:JSON.stringify(_d)}).catch(()=>{});}catch(_){}
    // #endregion
    if (isAppleCancelError(e)) {
      const cancelErr = new Error('APPLE_SIGNIN_CANCELED');
      (cancelErr as any).code = 'APPLE_SIGNIN_CANCELED';
      (cancelErr as any).isCanceled = true;
      throw cancelErr;
    }
    // Tag system-level Apple errors so callers can show a retry dialog
    if (isAppleSystemError(e)) {
      (e as any).isAppleSystemError = true;
    }
    throw e;
  }
  console.log('Apple login raw result:', res);
  // Handle nested response structure (seen in iOS logs)
  const data = res?.response || res;

  if (!data?.user) {
    throw new Error('Apple Sign-In failed: No user identifier received from Apple');
  }
  const fullName = data?.fullName ? `${data.fullName.givenName ?? ''} ${data.fullName.familyName ?? ''}`.trim() : undefined;
  console.log('Apple full name:', fullName);
  return {
    id: data.user,
    email: data?.email,
    full_name: fullName,
    profile_photo_url: undefined,
  };
}



export function loadAuthUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem('auth-user');
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function saveAuthUser(user: AuthUser | null) {
  try {
    if (user) localStorage.setItem('auth-user', JSON.stringify(user));
    else localStorage.removeItem('auth-user');
  } catch { }
}

/**
 * 删除账户 - 清除所有用户相关的本地存储数据
 * 符合 Apple App Store 审核要求 Guideline 5.1.1(v)
 */
export function deleteAccount(userId?: string | null): void {
  try {
    const baseKeys = [
      'auth-user',
      'jwt_token',
      't2s_conversations',
      't2s_daily_summaries',
      't2s_emotion_records',
      't2s_persona_style',
      't2s_celebration_shown_date',
      'onboarding-data',
      'onboarding-completed',
      'app-settings',
      'whisper-tutorial-v1',
      'ai-data-consent-v1',
    ];

    // 同时清除带用户 ID 后缀的 key，避免再次登录时读到旧数据
    const userKeys = userId ? [
      `onboarding-completed_${userId}`,
      `onboarding-data_${userId}`,
      `t2s_conversations_${userId}`,
      `t2s_daily_summaries_${userId}`,
      `t2s_emotion_records_${userId}`,
      `t2s_persona_style_${userId}`,
      `t2s_celebration_shown_date_${userId}`,
      `whisper-tutorial-v1_${userId}`,
    ] : [];

    [...baseKeys, ...userKeys].forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn(`Failed to remove ${key} from localStorage:`, e);
      }
    });

    console.log('Account deleted: All user data cleared from localStorage');
  } catch (error) {
    console.error('Error deleting account:', error);
    throw error;
  }
}

// ============================================================================
// JWT Token Management
// ============================================================================

/**
 * 获取后端 Auth 服务器地址
 * 优先使用 VITE_AUTH_SERVER_URL；若未设置则从 WebSocket URL 派生（向后兼容）。
 * 生产环境必须设置 VITE_AUTH_SERVER_URL 指向 HTTPS 公网地址，避免用语音服务器兜底。
 */
function getAuthServerUrl(): string {
  const explicit = (import.meta as any).env?.VITE_AUTH_SERVER_URL as string | undefined;
  if (explicit?.trim()) return explicit.trim();

  const wsUrl = getRealtimeVoiceServerUrl();
  const derived = wsUrl
    .replace('ws://', 'http://')
    .replace('wss://', 'https://')
    .replace('/ws/control', '');
  // #region agent log H-A: auth URL derivation
  try{const _e=(import.meta as any).env;const _d={sessionId:'9ab244',loc:'auth.ts:getAuthServerUrl',msg:'url derived',wsUrl,derived,viteRealtime:_e?.VITE_REALTIME_VOICE_SERVER_URL||'NOT_SET',viteAuthUrl:_e?.VITE_AUTH_SERVER_URL||'NOT_SET',isHttp:derived.startsWith('http://'),ts:Date.now(),hyp:'H-A'};try{const _p=JSON.parse(localStorage.getItem('_dbg9ab244')||'[]');_p.push(_d);localStorage.setItem('_dbg9ab244',JSON.stringify(_p));}catch(_){}fetch('http://127.0.0.1:7791/ingest/26a0facd-5b8c-473d-9da8-40faa6cbd7e3',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'9ab244'},body:JSON.stringify(_d)}).catch(()=>{});}catch(_){}
  // #endregion
  return derived;
}

/**
 * 登录并获取 JWT Token
 * 
 * 在用户完成 Google/Apple 登录后调用此函数，
 * 将 OAuth 凭证发送到后端换取 JWT Token
 * 
 * @param user - Google/Apple 登录返回的用户信息
 * @param provider - 登录提供商 ('google' 或 'apple')
 * @returns JWT Token 信息
 */
export async function getJWTTokenFromBackend(
  user: AuthUser,
  provider: 'google' | 'apple'
): Promise<JWTTokenResponse> {
  const authServerUrl = getAuthServerUrl();



  console.log(`[Auth] Requesting JWT token from ${authServerUrl}/auth/token`);

  // AbortSignal.timeout is only available iOS 16+; use a manual controller for iOS 14/15 compat
  function makeTimeoutSignal(ms: number): AbortSignal {
    if (typeof AbortSignal.timeout === 'function') return AbortSignal.timeout(ms);
    const ctrl = new AbortController();
    setTimeout(() => ctrl.abort(), ms);
    return ctrl.signal;
  }

  // Pre-flight health check (non-blocking, only for diagnostics)
  fetch(`${authServerUrl}/auth/health`, { signal: makeTimeoutSignal(5000) })
    .then(r => console.log(`[Auth] Health check: ${r.status}`))
    .catch(e => console.warn('[Auth] Health check failed:', e.message));

  const doTokenRequest = async (): Promise<Response> => {
    return fetch(`${authServerUrl}/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        provider: provider,
        oauth_id: user.id,
        email: user.email || undefined,
        name: user.full_name || undefined,
      }),
      signal: makeTimeoutSignal(15000),
    });
  };

  try {
    let response = await doTokenRequest().catch(async (e) => {
      // One retry after 1 s for transient network issues (common during app resume)
      console.warn('[Auth] Token request failed, retrying in 1s:', e.message);
      await new Promise(r => setTimeout(r, 1000));
      return doTokenRequest();
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[Auth] Token request failed: ${response.status} ${response.statusText} — ${errorText}`);
      const err = new Error(`Failed to get JWT token: ${response.status} ${response.statusText}`);
      (err as any).isNetworkError = true;
      throw err;
    }

    const data: JWTTokenResponse = await response.json();
    console.log('[Auth] JWT token received:', {
      user_id: data.user_id,
      expires_in: data.expires_in,
      token_type: data.token_type,
    });

    // Set AppsFlyer Customer User ID（仅原生；网页无 AppsFlyer 桥接）
    if (data.user_id && Capacitor.isNativePlatform()) {
      AppsFlyer.setCustomerUserId({ cuid: data.user_id })
        .then(() => console.log('[AppsFlyer] Customer User ID set:', data.user_id))
        .catch(err => console.error('[AppsFlyer] Failed to set CUID:', err));
    }

    return data;
  } catch (error: any) {
    console.error('[Auth] Error getting JWT token message:', error.message);
    console.error('[Auth] Error getting JWT token stack:', error.stack);
    console.error('[Auth] Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
    // #region agent log H-A H-C: JWT fetch failure
    try{const _d={sessionId:'9ab244',loc:'auth.ts:getJWTTokenFromBackend:catch',msg:'JWT fetch failed',authServerUrl:getAuthServerUrl(),errMsg:error.message,errName:error.name,isHttp:getAuthServerUrl().startsWith('http://'),ts:Date.now(),hyp:'H-A,H-C'};try{const _p=JSON.parse(localStorage.getItem('_dbg9ab244')||'[]');_p.push(_d);localStorage.setItem('_dbg9ab244',JSON.stringify(_p));}catch(_){}fetch('http://127.0.0.1:7791/ingest/26a0facd-5b8c-473d-9da8-40faa6cbd7e3',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'9ab244'},body:JSON.stringify(_d)}).catch(()=>{});}catch(_){}
    // #endregion
    throw error;
  }
}

/**
 * 完整的登录流程：OAuth 登录 + 获取 JWT Token
 * 
 * @param provider - 登录提供商 ('google' 或 'apple')
 * @returns 用户信息和 JWT Token
 */
export async function loginAndGetToken(
  provider: 'google' | 'apple'
): Promise<{ user: AuthUser; token: JWTTokenResponse }> {
  console.log(`[Auth] Starting login flow with ${provider}`);

  // 1. OAuth 登录
  let user: AuthUser;
  if (provider === 'google') {
    user = await loginWithGoogle();
  } else if (provider === 'apple') {
    user = await loginWithApple();
  } else {
    throw new Error('Invalid provider');
  }

  console.log('[Auth] OAuth login successful:', user);

  // 2. 保存用户信息（若后续换 JWT 失败则回滚，避免出现「有 auth-user 却无 jwt_token」导致 API 全失败）
  saveAuthUser(user);

  try {
    // 3. 获取 JWT Token
    const token = await getJWTTokenFromBackend(user, provider);

    // 3.5 Update user with whitelist status if present
    if (token.is_whitelisted !== undefined) {
      user.is_whitelisted = token.is_whitelisted;
      saveAuthUser(user);
      console.log('[Auth] Updated user whitelist status:', user.is_whitelisted);
    }

    // 4. 保存 JWT Token
    saveJWTToken(token.jwt_token);

    console.log('[Auth] Login flow completed successfully');

    // Analytics: Track Login Success
    logEvent('login_success', {
      provider: provider
    });

    return { user, token };
  } catch (e) {
    saveAuthUser(null);
    clearJWTToken();
    throw e;
  }
}

/**
 * 获取当前保存的 JWT Token
 */
export function getJWTToken(): string | null {
  try {
    return localStorage.getItem('jwt_token');
  } catch {
    return null;
  }
}

/**
 * 保存 JWT Token 到本地存储
 */
export function saveJWTToken(token: string): void {
  try {
    localStorage.setItem('jwt_token', token);
    console.log('[Auth] JWT token saved to localStorage');
  } catch (error) {
    console.error('[Auth] Failed to save JWT token:', error);
  }
}

/**
 * 清除 JWT Token
 */
export function clearJWTToken(): void {
  try {
    localStorage.removeItem('jwt_token');
    console.log('[Auth] JWT token cleared from localStorage');
  } catch (error) {
    console.error('[Auth] Failed to clear JWT token:', error);
  }
}

/**
 * 验证 JWT Token 是否有效
 */
export async function verifyJWTToken(token?: string): Promise<boolean> {
  const tokenToVerify = token || getJWTToken();

  if (!tokenToVerify) {
    console.log('[Auth] No token to verify');
    return false;
  }

  const authServerUrl = getAuthServerUrl();

  try {
    const response = await fetch(`${authServerUrl}/auth/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token: tokenToVerify }),
    });

    if (!response.ok) {
      console.log('[Auth] Token verification failed:', response.status);
      return false;
    }

    const data = await response.json();
    const isValid = data.valid === true;

    // Sync whitelist status if present in verification response
    if (isValid && data.is_whitelisted !== undefined) {
      const currentUser = loadAuthUser();
      if (currentUser && currentUser.is_whitelisted !== data.is_whitelisted) {
        console.log('[Auth] Syncing whitelist status from verify:', data.is_whitelisted);
        currentUser.is_whitelisted = data.is_whitelisted;
        saveAuthUser(currentUser);
      }
    }

    console.log('[Auth] Token verification result:', isValid);
    return isValid;
  } catch (error) {
    console.error('[Auth] Error verifying token:', error);
    return false;
  }
}

/**
 * 刷新 JWT Token
 * 
 * 在 Token 即将过期时调用，获取新的 Token
 */
export async function refreshJWTToken(): Promise<JWTTokenResponse> {
  const currentToken = getJWTToken();

  if (!currentToken) {
    throw new Error('No token to refresh. Please login first.');
  }

  const authServerUrl = getAuthServerUrl();

  console.log('[Auth] Refreshing JWT token...');

  try {
    const response = await fetch(`${authServerUrl}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token: currentToken }),
    });

    if (!response.ok) {
      // Token 已过期或无效，需要重新登录
      clearJWTToken();
      throw new Error('Token expired or invalid. Please login again.');
    }

    const data: JWTTokenResponse = await response.json();

    // 保存新 Token
    saveJWTToken(data.jwt_token);

    console.log('[Auth] JWT token refreshed successfully');

    return data;
  } catch (error) {
    console.error('[Auth] Error refreshing token:', error);
    throw error;
  }
}

/**
 * 检查用户是否已认证（有有效的 JWT Token）
 */
export async function isAuthenticated(): Promise<boolean> {
  const token = getJWTToken();
  if (!token) {
    return false;
  }

  // 验证 Token 是否有效
  return await verifyJWTToken(token);
}

/**
 * 登出 - 清除所有认证信息
 */
export async function logout(): Promise<void> {
  console.log('[Auth] Logging out...');

  // 1. Native Logout (Clear Session)
  try {
    await SocialLogin.logout({ provider: 'google' }).catch(() => { });
    console.log('[Auth] SocialLogin Google logout requested');
  } catch (e) {
    console.warn('[Auth] Failed to call SocialLogin logout:', e);
  }

  // 2. Clear Local Data
  clearJWTToken();
  saveAuthUser(null);
  console.log('[Auth] Logout completed');
}

